import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { TournamentCard, Tournament } from '../components/TournamentBanner';
import { http } from '../../api/http';

// Server response types
interface TournamentsResponse {
  tournaments: Tournament[];
}

interface TournamentHistory {
  tournamentId: string;
  tournamentName: string;
  placement: number;
  xpEarned: number;
  totalPlayers: number;
  completedAt: string;
}

interface PlayerTournamentsResponse {
  active: { tournament: Tournament; registration: { wins: number; losses: number } }[];
  history: TournamentHistory[];
}

interface TournamentMatch {
  id: string;
  round: number;
  matchNumber: number;
  player1Wallet: string | null;
  player2Wallet: string | null;
  winnerWallet: string | null;
  player1Score: number | null;
  player2Score: number | null;
  status: 'pending' | 'in_progress' | 'completed' | 'bye';
}

interface TournamentStanding {
  wallet: string;
  wins: number;
  losses: number;
  gameWins: number;
  rank: number;
}

interface TournamentDetailResponse {
  tournament: Tournament;
  matches: TournamentMatch[];
  standings: TournamentStanding[];
  isRegistered: boolean;
}

// Placement badge component
const PlacementBadge: React.FC<{ placement: number }> = ({ placement }) => {
  const config = {
    1: { icon: '🥇', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
    2: { icon: '🥈', color: 'text-gray-300', bg: 'bg-gray-400/20' },
    3: { icon: '🥉', color: 'text-amber-600', bg: 'bg-amber-600/20' },
  }[placement] || { icon: `#${placement}`, color: 'text-white/60', bg: 'bg-white/10' };

  return (
    <span
      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-bold ${config.color} ${config.bg}`}
    >
      {typeof config.icon === 'string' && config.icon.startsWith('#') ? config.icon : config.icon}
    </span>
  );
};

// Tournament rules section
const TournamentRules: React.FC<{ format: string }> = ({ format }) => (
  <div className="arcade-panel pixel-corners rounded-xl p-4 space-y-3">
    <h3 className="font-display text-sm font-bold text-white">HOW IT WORKS</h3>
    <div className="space-y-2 text-xs text-white/70">
      <div className="flex items-start gap-2">
        <span className="text-arcade-cyan">1.</span>
        <span>Register before the deadline to secure your spot</span>
      </div>
      {format === 'swiss' ? (
        <>
          <div className="flex items-start gap-2">
            <span className="text-arcade-cyan">2.</span>
            <span>Swiss format: Play all rounds, paired by record</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-arcade-cyan">3.</span>
            <span>Win = 3 points, Draw = 1 point, Loss = 0 points</span>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-start gap-2">
            <span className="text-arcade-cyan">2.</span>
            <span>Single elimination: Lose once and you're out</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-arcade-cyan">3.</span>
            <span>Win to advance to the next round</span>
          </div>
        </>
      )}
      <div className="flex items-start gap-2">
        <span className="text-arcade-cyan">4.</span>
        <span>Top finishers earn XP rewards and glory!</span>
      </div>
    </div>
  </div>
);

// Bracket visualization for single elimination
const BracketView: React.FC<{ matches: TournamentMatch[]; rounds: number }> = ({
  matches,
  rounds,
}) => {
  const roundMatches = Array.from({ length: rounds }, (_, i) =>
    matches.filter((m) => m.round === i + 1).sort((a, b) => a.matchNumber - b.matchNumber),
  );

  const truncateWallet = (wallet: string | null) => {
    if (!wallet) return 'TBD';
    return `${wallet.slice(0, 4)}...${wallet.slice(-4)}`;
  };

  return (
    <div className="arcade-panel pixel-corners rounded-xl p-4">
      <h3 className="font-display text-sm font-bold text-white mb-4">BRACKET</h3>
      <div className="flex gap-4 overflow-x-auto pb-2">
        {roundMatches.map((roundM, roundIdx) => (
          <div key={roundIdx} className="flex-shrink-0 space-y-2">
            <div className="text-xs text-white/40 text-center mb-2">
              {roundIdx === rounds - 1
                ? 'FINALS'
                : roundIdx === rounds - 2
                  ? 'SEMIS'
                  : `ROUND ${roundIdx + 1}`}
            </div>
            {roundM.map((match) => (
              <div
                key={match.id}
                className={`rounded-lg border p-2 space-y-1 min-w-[140px] ${
                  match.status === 'completed'
                    ? 'border-arcade-green/30 bg-arcade-green/5'
                    : match.status === 'in_progress'
                      ? 'border-arcade-yellow/50 bg-arcade-yellow/10'
                      : 'border-white/10 bg-white/5'
                }`}
              >
                <div
                  className={`text-xs px-2 py-1 rounded ${
                    match.winnerWallet === match.player1Wallet && match.status === 'completed'
                      ? 'bg-arcade-green/20 text-arcade-green'
                      : 'text-white/70'
                  }`}
                >
                  {truncateWallet(match.player1Wallet)}
                  {match.player1Score !== null && (
                    <span className="float-right">{match.player1Score}</span>
                  )}
                </div>
                <div className="text-xs text-white/30 text-center">vs</div>
                <div
                  className={`text-xs px-2 py-1 rounded ${
                    match.winnerWallet === match.player2Wallet && match.status === 'completed'
                      ? 'bg-arcade-green/20 text-arcade-green'
                      : 'text-white/70'
                  }`}
                >
                  {truncateWallet(match.player2Wallet)}
                  {match.player2Score !== null && (
                    <span className="float-right">{match.player2Score}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

// Swiss standings table
const SwissStandings: React.FC<{ standings: TournamentStanding[] }> = ({ standings }) => {
  const truncateWallet = (wallet: string) => `${wallet.slice(0, 4)}...${wallet.slice(-4)}`;

  return (
    <div className="arcade-panel pixel-corners rounded-xl p-4">
      <h3 className="font-display text-sm font-bold text-white mb-4">STANDINGS</h3>
      <div className="space-y-1">
        <div className="grid grid-cols-5 gap-2 text-xs text-white/40 pb-2 border-b border-white/10">
          <span>#</span>
          <span className="col-span-2">PLAYER</span>
          <span className="text-center">W-L</span>
          <span className="text-center">PTS</span>
        </div>
        {standings.map((standing, idx) => (
          <div
            key={standing.wallet}
            className={`grid grid-cols-5 gap-2 text-xs py-2 ${
              idx < 4 ? 'text-arcade-yellow' : 'text-white/70'
            }`}
          >
            <span>{standing.rank || idx + 1}</span>
            <span className="col-span-2 font-mono">{truncateWallet(standing.wallet)}</span>
            <span className="text-center">
              {standing.wins}-{standing.losses}
            </span>
            <span className="text-center font-bold">{standing.wins * 3}</span>
          </div>
        ))}
        {standings.length === 0 && (
          <div className="text-center text-white/40 py-4">No standings yet</div>
        )}
      </div>
    </div>
  );
};

// Tournament Detail Page
const TournamentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { publicKey } = useWallet();
  const wallet = publicKey?.toBase58();

  const [data, setData] = useState<TournamentDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTournament = useCallback(async () => {
    if (!id) return;
    try {
      setError(null);
      const params = wallet ? `?wallet=${wallet}` : '';
      const response = await http.get<TournamentDetailResponse>(`tournaments/${id}${params}`);
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tournament');
    } finally {
      setLoading(false);
    }
  }, [id, wallet]);

  useEffect(() => {
    fetchTournament();
  }, [fetchTournament]);

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-8">
        <div className="text-center text-white/50 animate-pulse">Loading tournament...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-8">
        <div className="arcade-panel pixel-corners rounded-xl p-8 text-center">
          <div className="text-4xl mb-2">😵</div>
          <div className="text-red-400 mb-4">{error || 'Tournament not found'}</div>
          <button onClick={() => navigate('/tournaments')} className="arcade-btn px-4 py-2 text-xs">
            Back to Tournaments
          </button>
        </div>
      </div>
    );
  }

  const { tournament, matches, standings } = data;
  const totalXpPrize = tournament.xpPrizes
    ? Object.values(tournament.xpPrizes).reduce((sum, xp) => sum + xp, 0)
    : 0;

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      {/* Header */}
      <motion.div initial={{ y: -20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="mb-8">
        <Link
          to="/tournaments"
          className="text-white/50 text-xs hover:text-white mb-2 inline-block"
        >
          ← Back to Tournaments
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <span className="text-4xl">🏆</span>
          <h1 className="neon-text-pink font-display text-3xl">{tournament.name}</h1>
        </div>
        {tournament.description && (
          <p className="text-sm text-white/60">{tournament.description}</p>
        )}
      </motion.div>

      {/* Tournament Card + Info */}
      <div className="grid gap-6 md:grid-cols-2 mb-6">
        <TournamentCard
          tournament={{ ...tournament, isRegistered: data.isRegistered }}
          onRegister={fetchTournament}
          wallet={wallet}
        />
        <TournamentRules format={tournament.format} />
      </div>

      {/* Prizes */}
      <div className="arcade-panel pixel-corners rounded-xl p-4 mb-6">
        <h3 className="font-display text-sm font-bold text-white mb-3">PRIZES</h3>
        <div className="grid grid-cols-4 gap-2 text-center text-xs">
          {Object.entries(tournament.xpPrizes || {})
            .sort(([a], [b]) => parseInt(a) - parseInt(b))
            .map(([place, xp]) => {
              const icons: Record<string, string> = { '1': '🥇', '2': '🥈', '3': '🥉' };
              return (
                <div key={place}>
                  <div className={place === '1' ? 'text-yellow-400' : 'text-white/60'}>
                    {icons[place] || `#${place}`}
                  </div>
                  <div className="text-arcade-yellow font-bold">{xp} XP</div>
                </div>
              );
            })}
        </div>
        {tournament.prizeType === 'sol' && tournament.prizePoolLamports > 0 && (
          <div className="text-center text-arcade-green mt-3">
            Prize Pool: {(tournament.prizePoolLamports / 1e9).toFixed(2)} SOL
          </div>
        )}
      </div>

      {/* Bracket or Standings based on format */}
      {tournament.format === 'single_elim' || tournament.format === 'double_elim' ? (
        matches.length > 0 && <BracketView matches={matches} rounds={tournament.rounds} />
      ) : (
        <SwissStandings standings={standings} />
      )}
    </div>
  );
};

// Main Tournament List Page
const TournamentListPage: React.FC = () => {
  const { publicKey } = useWallet();
  const wallet = publicKey?.toBase58();

  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [playerData, setPlayerData] = useState<PlayerTournamentsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'upcoming' | 'history'>('upcoming');

  const fetchData = useCallback(async () => {
    try {
      const params = wallet ? `?wallet=${wallet}` : '';
      const tournamentsRes = await http.get<TournamentsResponse>(`tournaments${params}`);
      setTournaments(tournamentsRes.tournaments);

      if (wallet) {
        const playerRes = await http.get<PlayerTournamentsResponse>(
          `players/${wallet}/tournaments`,
        );
        setPlayerData(playerRes);
      }
    } catch (err) {
      console.error('Failed to fetch tournaments:', err);
    } finally {
      setLoading(false);
    }
  }, [wallet]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const upcomingTournaments = tournaments.filter(
    (t) => t.status === 'scheduled' || t.status === 'registration' || t.status === 'in_progress',
  );

  const registeredTournamentIds = new Set(playerData?.active.map((a) => a.tournament.id) || []);

  return (
    <div className="container mx-auto max-w-4xl px-4 py-8">
      {/* Header */}
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="mb-8 text-center"
      >
        <div className="mb-2 flex items-center justify-center gap-3">
          <span className="text-4xl">🏆</span>
          <h1 className="neon-text-pink font-display text-4xl">TOURNAMENTS</h1>
        </div>
        <p className="font-arcade text-sm text-white/60">Compete in events for glory and rewards</p>
      </motion.div>

      {/* Tab Selector */}
      <div className="mb-6 flex justify-center gap-2">
        <button
          onClick={() => setActiveTab('upcoming')}
          className={`arcade-panel pixel-corners flex items-center gap-2 rounded-lg px-4 py-2 font-pixel text-sm transition-all ${
            activeTab === 'upcoming'
              ? 'border-arcade-cyan bg-arcade-cyan/20 text-arcade-cyan'
              : 'border-white/20 text-white/60 hover:border-white/40 hover:text-white'
          }`}
        >
          <span>📅</span>
          <span>Upcoming</span>
        </button>
        <button
          onClick={() => setActiveTab('history')}
          className={`arcade-panel pixel-corners flex items-center gap-2 rounded-lg px-4 py-2 font-pixel text-sm transition-all ${
            activeTab === 'history'
              ? 'border-arcade-cyan bg-arcade-cyan/20 text-arcade-cyan'
              : 'border-white/20 text-white/60 hover:border-white/40 hover:text-white'
          }`}
        >
          <span>📜</span>
          <span>My History</span>
        </button>
      </div>

      {loading ? (
        <div className="text-center text-white/50 animate-pulse py-8">Loading tournaments...</div>
      ) : activeTab === 'upcoming' ? (
        <div className="grid gap-6 md:grid-cols-2">
          {/* Tournament List */}
          <div className="space-y-4">
            <h2 className="font-display text-sm text-white/50 uppercase tracking-wider">
              Upcoming Events
            </h2>
            {upcomingTournaments.length > 0 ? (
              upcomingTournaments.map((tournament) => (
                <motion.div
                  key={tournament.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <TournamentCard
                    tournament={{
                      ...tournament,
                      isRegistered: registeredTournamentIds.has(tournament.id),
                    }}
                    onRegister={fetchData}
                    wallet={wallet}
                  />
                </motion.div>
              ))
            ) : (
              <div className="arcade-panel pixel-corners rounded-xl p-6 text-center">
                <div className="text-4xl mb-2">😴</div>
                <div className="text-white/60">No upcoming tournaments</div>
                <div className="text-xs text-white/40 mt-1">Check back soon!</div>
              </div>
            )}
          </div>

          {/* Rules & Info */}
          <div className="space-y-4">
            <h2 className="font-display text-sm text-white/50 uppercase tracking-wider">
              Tournament Info
            </h2>
            <TournamentRules format="swiss" />

            {/* Your registered tournaments */}
            {registeredTournamentIds.size > 0 && (
              <div className="arcade-panel pixel-corners rounded-xl p-4 border-arcade-green/30 bg-arcade-green/10">
                <h3 className="font-display text-sm font-bold text-arcade-green mb-2">
                  ✓ YOU'RE REGISTERED
                </h3>
                <p className="text-xs text-white/70">
                  You're registered for {registeredTournamentIds.size} tournament
                  {registeredTournamentIds.size > 1 ? 's' : ''}. Be ready when the tournament
                  starts!
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <h2 className="font-display text-sm text-white/50 uppercase tracking-wider">
            Your Tournament History
          </h2>

          {!wallet ? (
            <div className="arcade-panel pixel-corners rounded-xl p-8 text-center">
              <div className="text-4xl mb-2">🔗</div>
              <div className="text-white/60 mb-2">Connect your wallet</div>
              <div className="text-xs text-white/40">to view your tournament history</div>
            </div>
          ) : playerData?.history && playerData.history.length > 0 ? (
            <div className="space-y-3">
              {playerData.history.map((result, idx) => (
                <motion.div
                  key={result.tournamentId}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="arcade-panel pixel-corners rounded-xl p-4"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <PlacementBadge placement={result.placement} />
                      <div>
                        <div className="font-display text-sm text-white">
                          {result.tournamentName}
                        </div>
                        <div className="text-xs text-white/40">
                          {new Date(result.completedAt).toLocaleDateString()} •{' '}
                          {result.totalPlayers} players
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-arcade-yellow font-bold">+{result.xpEarned} XP</div>
                    </div>
                  </div>
                </motion.div>
              ))}

              {/* Stats summary */}
              <div className="arcade-panel pixel-corners rounded-xl p-4">
                <h3 className="font-display text-xs text-white/50 mb-3">TOURNAMENT STATS</h3>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-xs text-white/40">PLAYED</div>
                    <div className="font-display text-lg text-white">
                      {playerData.history.length}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-white/40">BEST FINISH</div>
                    <div className="font-display text-lg text-arcade-yellow">
                      #{Math.min(...playerData.history.map((r) => r.placement))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-white/40">TOP 4 FINISHES</div>
                    <div className="font-display text-lg text-arcade-green">
                      {playerData.history.filter((r) => r.placement <= 4).length}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-white/40">XP EARNED</div>
                    <div className="font-display text-lg text-arcade-cyan">
                      {playerData.history.reduce((sum, r) => sum + r.xpEarned, 0)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="arcade-panel pixel-corners rounded-xl p-8 text-center">
              <div className="text-4xl mb-2">🎮</div>
              <div className="text-white/60 mb-2">No tournament history yet</div>
              <div className="text-xs text-white/40">
                Register for an upcoming tournament to get started!
              </div>
            </div>
          )}
        </div>
      )}

      {/* Back to Home */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-8 text-center"
      >
        <Link
          to="/"
          className="font-pixel text-sm text-white/60 transition-colors hover:text-white"
        >
          ← Back to Home
        </Link>
      </motion.div>
    </div>
  );
};

// Export main page component - will route to detail if ID present
export const TournamentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return id ? <TournamentDetailPage /> : <TournamentListPage />;
};
